import { memo, useEffect, useMemo, useState } from "react";
import { highlightInVizReducedOpacity, ColorBy } from "@atlas-int/data";
import styled from "@emotion/styled";
import { fadeIn, duration } from "@atlas-int/tokens";
import type { HierarchyRectangularNode } from "d3-hierarchy";
import type { EnrichedTradeDataItem } from "@atlas-int/data/types";
import type { OnHoverParams } from "../hooks";

const treemapRectangleStyle = {
  stroke: "#ffffff",
};

const treemapRectangleHighlightedStyle = {
  ...treemapRectangleStyle,
  opacity: 1,
};

const treemapRectangleUnhlightedStyle = {
  ...treemapRectangleStyle,
  opacity: highlightInVizReducedOpacity,
};

const TreemapGroup = styled.g`
  opacity: 1;
  transition: transform 0.5s;

  &.enter {
    opacity: 0;
    animation-name: ${fadeIn};
    animation-duration: ${duration.base};
    animation-timing-function: linear;
    // animation-delay: 0.3s;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }
`;

type TreemapLeaf = HierarchyRectangularNode<EnrichedTradeDataItem>;
type TreemapColorMap = Map<string, string> | ((value: number) => string);

interface TreemapRectangleProps {
  leaf: TreemapLeaf;
  colorMap: TreemapColorMap;
  useId: string | undefined;
  colorBy: ColorBy;
  useStyle: React.CSSProperties;
  width: number;
  height: number;
}

interface TreemapCellProps {
  leaf: TreemapLeaf;
  handleTooltip: (params: OnHoverParams) => void;
  colorMap: TreemapColorMap;
  useId: string | undefined;
  colorBy: ColorBy;
  highlightedItem?: string;
  servicesWidth?: number;
  nonServices?: boolean;
}

const TreemapRectangle = memo(
  ({ leaf, colorMap, useId, colorBy, useStyle, width, height }: TreemapRectangleProps) => {
    const useColor = useMemo(() => {
      if (colorBy === ColorBy.Sector) {
        const color = (colorMap as Map<string, string>).get(useId as string);
        return color;
      } else if (colorBy === ColorBy.Complexity) {
        return leaf.data.pci ? (colorMap as (value: number) => string)(leaf.data.pci as number) : "#CCCCCC";
      }
    }, [colorBy, colorMap, useId, leaf.data.pci]);

    return (
      <rect
        style={{
          ...useStyle,
          transition: "width 0.5s, height 0.5s",
        }}
        x="0"
        y="0"
        width={width}
        height={height}
        fill={useColor}
      />
    );
  },
);

const TreemapCell = ({
  leaf,
  handleTooltip,
  colorMap,
  useId,
  colorBy,
  highlightedItem,
  servicesWidth,
  nonServices,
}: TreemapCellProps) => {
  const xPosition = nonServices ? (servicesWidth ?? 0) + leaf.x0 : leaf.x0;
  const yPosition = leaf.y0;

  const cellWidth = leaf.x1 - leaf.x0;
  const cellHeight = leaf.y1 - leaf.y0;

  // Track whether this is the initial render to apply the enter animation class
  const [isEntering, setIsEntering] = useState(true);

  useEffect(() => {
    // After initial render, remove the enter class so subsequent updates
    // use CSS transitions instead of the fade-in animation
    const id = requestAnimationFrame(() => {
      setIsEntering(false);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const useColor = useMemo(() => {
    if (colorBy === ColorBy.Sector) {
      return (colorMap as Map<string, string>).get(useId as string);
    } else if (colorBy === ColorBy.Complexity) {
      return leaf.data.pci ? (colorMap as (value: number) => string)(leaf.data.pci as number) : "#CCCCCC";
    }
  }, [colorBy, colorMap, useId, leaf.data.pci]);

  const useStyle = useMemo(() => {
    if (
      !highlightedItem ||
      leaf.data.productId === highlightedItem ||
      leaf.data.id === highlightedItem
    ) {
      return treemapRectangleHighlightedStyle;
    } else {
      return treemapRectangleUnhlightedStyle;
    }
  }, [leaf.data.productId, leaf.data.id, highlightedItem]);

  const onMouseEnterHandler = useMemo(
    () => () => {
      // Only show hover tooltip if no tooltip is currently fixed
      if (!highlightedItem) {
        handleTooltip({
          datum: leaf.data,
          fillColor: useColor,
          setTooltipFixed: false,
          leaf: undefined,
        });
      }
    },
    [handleTooltip, leaf.data, useColor, highlightedItem],
  );

  const onClickHandler = useMemo(
    () => (e: React.MouseEvent) => {
      // Stop propagation to prevent SVG background click handler from firing
      e.stopPropagation();

      const isCurrentCellHighlighted =
        highlightedItem &&
        (leaf.data.productId === highlightedItem || leaf.data.id === highlightedItem);

      // If this cell is already highlighted (tooltip is fixed), close it
      if (isCurrentCellHighlighted) {
        handleTooltip({ datum: undefined });
        return;
      }

      // If another cell's tooltip is stuck, close it and don't open a new one
      if (highlightedItem) {
        handleTooltip({ datum: undefined });
        return;
      }

      // No tooltip is currently fixed, so fix one at click position
      // Use page coordinates directly - the tooltip handler will adjust for container offset
      const xLocation = e.clientX;
      const yLocation = e.clientY;

      handleTooltip({
        datum: leaf.data,
        leaf,
        setTooltipFixed: true,
        setTooltipCoords: [xLocation, yLocation],
        fillColor: useColor,
      });
    },
    [handleTooltip, leaf, useColor, highlightedItem],
  );

  const onMouseLeaveHandler = useMemo(
    () => () => {
      // Only clear tooltip on mouse leave if no tooltip is fixed
      if (!highlightedItem) {
        handleTooltip({ datum: undefined });
      }
    },
    [handleTooltip, highlightedItem],
  );

  return (
    <TreemapGroup
      className={isEntering ? "enter" : undefined}
      transform={`translate(${xPosition}, ${yPosition})`}
      onMouseEnter={onMouseEnterHandler}
      onMouseLeave={onMouseLeaveHandler}
      onClick={onClickHandler}
    >
      <TreemapRectangle
        leaf={leaf}
        colorMap={colorMap}
        useId={useId}
        colorBy={colorBy}
        useStyle={useStyle}
        width={cellWidth}
        height={cellHeight}
      />
    </TreemapGroup>
  );
};

export default memo(TreemapCell, (prev, next) => {
  return (
    prev.leaf === next.leaf &&
    prev.handleTooltip === next.handleTooltip &&
    prev.colorMap === next.colorMap &&
    prev.useId === next.useId &&
    prev.colorBy === next.colorBy &&
    prev.highlightedItem === next.highlightedItem &&
    prev.servicesWidth === next.servicesWidth &&
    prev.nonServices === next.nonServices
  );
});
